import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  MenuItem,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  List,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";

const UserEditModal = ({ open, onClose, user, onSave, currentUserId }) => {
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editUseremail, setEditUseremail] = useState("");
  const [editUserrole, setEditUserrole] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const roles = ["admin1", "admin2", "editor1", "editor2"];

  const passwordRules = {
    minLength: editPassword.length >= 6,
    hasUppercase: /[A-Z]/.test(editPassword),
    hasLowercase: /[a-z]/.test(editPassword),
    hasNumber: /[0-9]/.test(editPassword),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(editPassword),
  };

  useEffect(() => {
    if (user) {
      setEditUsername(user.username);
      setEditUseremail(user.email);
      setEditUserrole(user.role);
      setEditPassword("");
      setErrors({});
    }
  }, [user]);

  const renderChecklistItem = (condition, text) => (
    <ListItem>
      <ListItemIcon>
        {condition ? <CheckCircle color="success" /> : <Cancel color="error" />}
      </ListItemIcon>
      <ListItemText primary={text} />
    </ListItem>
  );

  const handleSave = () => {
    let newErrors = {};

    if (editPassword.trim() !== "") {
      if (
        !passwordRules.minLength ||
        !passwordRules.hasUppercase ||
        !passwordRules.hasLowercase ||
        !passwordRules.hasNumber ||
        !passwordRules.hasSpecialChar
      ) {
        newErrors.editPassword = "Password does not meet all requirements.";
      }
    }

    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(editUseremail)) {
      newErrors.editUseremail = "Email is not valid";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    onSave({
      _id: user._id,
      username: editUsername,
      password: editPassword,
      email: editUseremail,
      role: editUserrole,
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          p: 3,
          borderRadius: 2,
          minWidth: 300,
        }}
      >
        <Typography variant="h6">Edit User</Typography>
        <TextField
          label="Username"
          value={editUsername}
          onChange={(e) => setEditUsername(e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
        />
        <TextField
          label="Email"
          value={editUseremail}
          onChange={(e) => setEditUseremail(e.target.value)}
          type="email"
          fullWidth
          sx={{ mt: 2 }}
          error={Boolean(errors.editUseremail)}
          helperText={errors.editUseremail}
        />
        <TextField
          select
          label="Role"
          value={editUserrole}
          onChange={(e) => setEditUserrole(e.target.value)}
          fullWidth
          sx={{
            mt: 2,
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "black",
            },
            "& .MuiFormHelperText-root": {
              color: "black",
            },
          }}
          disabled={user?._id === currentUserId}
          helperText={
            user?._id === currentUserId
              ? "You cannot edit the current user's role. Please log in with another account to make changes."
              : ""
          }
        >
          {roles.map((role) => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="New Password"
          type={showPassword ? "text" : "password"}
          value={editPassword}
          onChange={(e) => setEditPassword(e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
          error={Boolean(errors.editPassword)}
          helperText={errors.editPassword}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    data-log="Show/Hide Password Icon Button"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <Box mb={1} p={0.5} border="1px solid #ccc" borderRadius={1}>
          <Typography variant="caption" fontWeight="bold" fontSize="0.75rem">
            Password must contain:
          </Typography>
          <List dense sx={{ paddingTop: 0, paddingBottom: 0 }}>
            {renderChecklistItem(
              passwordRules.minLength,
              "At least 6 characters"
            )}
            {renderChecklistItem(
              passwordRules.hasUppercase,
              "An uppercase letter (A-Z)"
            )}
            {renderChecklistItem(
              passwordRules.hasLowercase,
              "A lowercase letter (a-z)"
            )}
            {renderChecklistItem(passwordRules.hasNumber, "A number (0-9)")}
            {renderChecklistItem(
              passwordRules.hasSpecialChar,
              "A special character (!@#$...)"
            )}
          </List>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save
          </Button>
          <Button sx={{ ml: 1 }} onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UserEditModal;
